import {
    Box,
    Flex,
    Heading,
    IconButton,
    Icons,
    Image,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
    useDisclosure,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import ConfirmationDialog from "components/confirm-dialog";
import { useTranslation } from "react-i18next";
import { checkIsMyBookingPage } from "routes";
import { CSSVarHeaderHeight, ModalCloseButtonTop } from "style-variables";
import { isGuid } from "utils";
import { closeBookingModal } from "utils/modal-utils";
import { HamburgerButton } from "./hamburger-button";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { urlBuilder } from "search-params/url-builder";

interface IHeaderProps {
    shouldHideCoverImage: boolean;
    hasCoverImage: boolean;
    isModal: boolean;
    hasMyBookingBackgroundImage?: boolean;
}

const Header = ({
    shouldHideCoverImage,
    hasCoverImage,
    isModal,
    hasMyBookingBackgroundImage,
}: IHeaderProps) => {
    const { isLoading, error, data } = useExternalBookingSettings();
    const { t } = useTranslation();
    const location = useLocation();
    const [isMyBookingPage, setIsMyBookingPage] = useState(false);

    useEffect(() => {
        const isMyBooking = checkIsMyBookingPage(location.pathname);
        setIsMyBookingPage(isMyBooking);
    }, [location.pathname]);

    const hasGuid = () => {
        const locationPathnames = location.pathname.split("/");
        return locationPathnames.some((path) => isGuid(path));
    };

    const { isOpen, onOpen, onClose } = useDisclosure();
    const shouldUseTransparentHeader = hasMyBookingBackgroundImage && isMyBookingPage;

    const showHeader = shouldHideCoverImage || !hasCoverImage;
    const getHeaderBGColor = () => {
        if (!shouldUseTransparentHeader && showHeader) {
            return "var(--theme-colors-primary-wlv)";
        }

        return "none";
    };

    /**
     * If the user made any choices we prompt the user
     */
    const closeApplicationModal = () => {
        const currentParams = new URLSearchParams(window.location.search);
        const hasGuests = currentParams.get("guests") !== null;

        if (hasGuests || hasGuid()) {
            onOpen();
        } else {
            closeBookingModal();
        }
    };

    const exitDialogKey = isMyBookingPage ? "exitWarningDialog.finalized" : "exitWarningDialog";
    const exitPrimaryText = t(`${exitDialogKey}.exitButton`);
    const exitSecondaryText = t(`${exitDialogKey}.stayButton`);
    const exitMessage = t(`${exitDialogKey}.message`);
    const exitTitle = t(`${exitDialogKey}.title`);
    const logoUrl = data?.themeSettings?.logo.imageUrl;
    const imageParams: Record<string, string | undefined> = {
        Type: "webp",
    };
    const svgLogoBaseUrl = data?.themeSettings.svgLogo.image
        ? `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${data.themeSettings.svgLogo.image}`
        : undefined;
    const svgLogoUrl = svgLogoBaseUrl ? urlBuilder(svgLogoBaseUrl, imageParams) : undefined;
    const useLeftAlignLogotype = !!svgLogoUrl;

    return !isLoading && data ? (
        <Box
            display="flex"
            as="header"
            position={showHeader ? "relative" : "absolute"}
            top="0"
            zIndex={3}
            width="100%"
            h={CSSVarHeaderHeight}
            p={ThemeSpaceVariable.Medium}
            px={{ base: ThemeSpaceVariable.Medium, lg: ThemeSpaceVariable.XLarge }}
            alignItems="center"
            backgroundColor={getHeaderBGColor()}
            // @ts-expect-error // ! TODO: Include transition in ThemeStyleProps for box in casper-ui-library
            transition={!shouldHideCoverImage ? "background-color 0.2s ease-in-out" : ""}
        >
            {!isModal && !useLeftAlignLogotype && (
                <Box display="flex" justifyContent="flex-start" flex={1}>
                    {!error && <HamburgerButton />}
                </Box>
            )}
            <Flex justifyContent={useLeftAlignLogotype ? "flex-start" : "center"} flex={3}>
                <Heading
                    as={!isModal ? "h1" : "h2"}
                    color={ThemeColorVariable.OnPrimary}
                    fontSize={{
                        base: ThemeFontSizeVariable.XLarge,
                        md: ThemeFontSizeVariable.X2Large,
                    }}
                    fontFamily={ThemeFontVariable.Header}
                    fontWeight="bold"
                    textAlign="center"
                    noOfLines={2}
                >
                    {logoUrl || svgLogoUrl ? (
                        <Image
                            src={useLeftAlignLogotype ? svgLogoUrl : logoUrl}
                            zIndex="2"
                            maxHeight={CSSVarHeaderHeight}
                            alt={data.unitsMetaData.name}
                        />
                    ) : (
                        data.unitsMetaData.name
                    )}
                </Heading>
            </Flex>
            {isModal ? (
                <Box
                    tabIndex={0}
                    position="fixed"
                    right={ThemeSpaceVariable.Medium}
                    top={ModalCloseButtonTop}
                >
                    <IconButton
                        aria-label={t("application.closeBookingModal")}
                        size="md"
                        icon={Icons.Close}
                        onClick={closeApplicationModal}
                        tabIndex={0}
                        data-track-click="exit-booking"
                    />
                </Box>
            ) : useLeftAlignLogotype ? (
                <Box display="flex" justifyContent="flex-end" flex={1}>
                    {!error && <HamburgerButton isLeftAligned={false} />}
                </Box>
            ) : (
                <Flex flex="1" />
            )}
            <ConfirmationDialog
                primaryButtonText={exitPrimaryText}
                secondaryButtonText={exitSecondaryText}
                isOpen={isOpen}
                title={exitTitle}
                message={exitMessage}
                onClose={onClose}
                secondaryAction={onClose}
                primaryAction={closeBookingModal}
            />
        </Box>
    ) : null;
};

export default Header;
